import React from 'react';
import { CheckCircle, Star, DollarSign, MapPin } from 'lucide-react';

/**
 * Client information row for job cards
 * Displays payment verification, rating, spending, and location
 */
const ClientInfoRow = ({
  paymentVerified = false,
  rating,
  reviewCount,
  totalSpent,
  location,
  className = ''
}) => {
  // Format total spent
  const formatSpent = (amount) => {
    if (!amount) return null;
    if (typeof amount === 'string') return amount;

    // Handle object format like { value: 1000, currency: 'USD' }
    if (typeof amount === 'object') {
      const value = amount.value ?? amount.amount ?? amount.rawValue;
      if (!value && value !== 0) return null;
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M+`;
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K+`;
      return `$${value}`;
    }

    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M+`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K+`;
    return `$${amount}`;
  };

  // Render star rating
  const renderRating = () => {
    if (rating === undefined || rating === null) return null;

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center gap-1">
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-3 h-3 ${
                i < fullStars
                  ? 'text-yellow-400 fill-current'
                  : i === fullStars && hasHalfStar
                  ? 'text-yellow-400 fill-current opacity-50'
                  : 'text-gray-300'
              }`}
            />
          ))}
        </div>
        <span className="text-gray-600 text-xs">
          {rating.toFixed(1)}
          {reviewCount !== undefined && ` (${reviewCount})`}
        </span>
      </div>
    );
  };

  const items = [];

  // Payment verified
  if (paymentVerified) {
    items.push(
      <div key="payment" className="flex items-center gap-1 text-gray-600">
        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
        <span className="text-xs">Payment verified</span>
      </div>
    );
  }

  // Rating
  if (rating !== undefined && rating !== null) {
    items.push(
      <div key="rating">
        {renderRating()}
      </div>
    );
  }

  // Total spent
  const formattedSpent = formatSpent(totalSpent);
  if (formattedSpent) {
    items.push(
      <div key="spent" className="flex items-center gap-1 text-gray-600">
        <DollarSign className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs">{formattedSpent} spent</span>
      </div>
    );
  }

  // Location
  if (location) {
    items.push(
      <div key="location" className="flex items-center gap-1 text-gray-600">
        <MapPin className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs">{location}</span>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className="text-gray-300 hidden sm:inline">•</span>}
          {item}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ClientInfoRow;
